import { Howl, Howler } from 'howler';

const instances = {};

export default class AudioHowlerPlayer {
    /**
     *
     * @param {Howler} howlerAudio
     * @param {integer} assessmentRunId
     * @param {function} onEndCallback
     * @return {AudioHowlerPlayer}
     */
    static createInstanceFromHowlerAudio(howlerAudio, partStartTimes, assessmentRunId, onEndCallback) {
        return new AudioHowlerPlayer(howlerAudio, partStartTimes, assessmentRunId, onEndCallback);
    }

    /**
     * @param {integer} assessmentRunId
     * @param {Array<string>} urls
     * @param {Array<string>} formats
     * @return {AudioHowlerPlayer}
     */
    static createInstance(assessmentRunId, onEndCallback, urls, formats, partStartTimes = []) {
        Howler.autoUnlock = true;
        const howlerAudio = new Howl({
            src: urls,
            format: formats,
            autoplay: false,
            loop: false,
            preload: true,
            volume: 1.0,
            html5: true,
        });

        return AudioHowlerPlayer.createInstanceFromHowlerAudio(howlerAudio, partStartTimes, assessmentRunId, onEndCallback);
    }

    static getInstanceForAssessmentRunId(assessmentRunId) {
        return instances[assessmentRunId];
    }

    constructor(howlerAudio, partStartTimes, assessmentRunId, onEndCallback) {
        this.assessmentRunId = assessmentRunId;
        this.partStartTimes = partStartTimes;
        this.audioEnded = false;
        this.howlerAudio = howlerAudio;
        this.onEndCallback = onEndCallback;

        this.howlerAudio.once('playerror', (id, error) => {
            console.error('Player error', error);
        });

        this.howlerAudio.on('end', () => {
            this.audioEnded = true;
            if (this.onEndCallback) {
                this.onEndCallback();
            }
        });

        instances[assessmentRunId] = this;
    }

    setHowler(howlerAudio) {
        this.howlerAudio = howlerAudio;
    }

    playAudio(audioPosition = 0) {
        if (audioPosition > 0) {
            this.howlerAudio.seek(audioPosition);
        }
        this.howlerAudio.play();
    }

    pauseAudio() {
        this.howlerAudio.pause();
    }

    resumeAudio() {
        if (!this.howlerAudio.playing()) {
            this.howlerAudio.play();
        }
    }

    seekAudio(audioPosition) {
        this.audioEnded = false;
        this.howlerAudio.seek(audioPosition);
        this.howlerAudio.play();
    }

    seekAudioWhenLoaded(audioPosition) {
        this.audioEnded = false;
        const doSeekAndPlay = () => {
            this.howlerAudio.seek(audioPosition);
            this.howlerAudio.play();
        };
        if (this.howlerAudio.state() === 'loaded') {
            doSeekAndPlay();
            return;
        }
        this.howlerAudio.once('load', doSeekAndPlay);
    }

    stopAudio() {
        this.howlerAudio.stop();
    }

    stopAndRemoveAudio() {
        this.howlerAudio.unload();
    }

    getAudioTime() {
        return this.audioHasEnded() ? this.getAudioDuration() : this.howlerAudio?.seek();
    }

    getAudioDuration() {
        return this.howlerAudio?.duration();
    }

    audioHasEnded() {
        return this.audioEnded;
    }

    getAudioPartStartTime(partId) {
        if (!this.partStartTimes) {
            return null;
        }

        const audioPart = this.partStartTimes.find(part => part.contentItemId === partId);
        const partStartTime = audioPart?.startTime;
        return partStartTime === undefined ? null : partStartTime;
    }
}
